import type {
  ChatActivityAnswer,
  ExecutiveSummaryAnswer,
  InputStyle4Answer,
  PersonaEntryAnswer,
} from '@app/@types/redux/answer';
import type {
  CanvasOptions,
  ChatActivityOptions,
  TextareaOptions,
} from '@app/@types/redux/pageElement';
import type { DependeeValue } from '@app/@types/redux/project';
import type { PageElementProps } from '@components/Projects/TakeProject';
import { PAGE_ELEMENT } from '@libs/constants/constants';
import logger from '@libs/log';
import { nanoid } from '@reduxjs/toolkit';
import { useAppSelector } from '@state/redux/store';
import { getDependeeValues, getPageElementDisabledState } from '@state/selectors/projects';
import { getCompletions, getGenerativeDataFromPrompt, type Message } from '@state/utils/openai';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import ChatBox from './ChatBox';
import ChatMenu from './ChatMenu';
import type { ChatItem } from './TempChat';
import './index.scss';
import { Chats } from './tempConstants';

const Chat: React.FC<PageElementProps> = (props) => {
  const { answer, pageElement, state } = props;

  const currentAnswer = props.currentAnswer as ChatActivityAnswer | undefined;

  const { dependees } = pageElement.attributes;

  const dependentValues = getDependeeValues(dependees, [], state) as DependeeValue[];
  const disabled = getPageElementDisabledState(state, pageElement.id);

  const [dependentAnswer, setDependentAnswer] = useState('');
  const [modelAnswer, setModelAnswer] = useState('');

  useEffect(() => {
    if (!currentAnswer?.dependentAnswer && dependentValues.length > 0) {
      let dependentAnswer = '';
      let modelAnswer = '';
      if (dependentValues[0].dependeePageElement.elementType === PAGE_ELEMENT.PERSONA_ENTRY) {
        const personaEntry = dependentValues[0].answer as PersonaEntryAnswer;
        const inputFieldAnswers = personaEntry.inputFields
          .map((field) => `${field.fieldLabel}: ${field.fieldValue}`)
          .join('\n');
        const longTextareaAnswers = personaEntry.longTextareaFields
          .map((field) => `${field.fieldLabel}: ${field.fieldValue}`)
          .join('\n');

        dependentAnswer = `Input Fields:\n${inputFieldAnswers}\nLong Textarea Fields:\n${longTextareaAnswers}`;
      }
      if (
        (dependentValues[0].dependeePageElement.options as CanvasOptions).canvasType ===
        'swot_canvas_creator'
      ) {
        const strengths = (
          dependentValues.find(
            (dv) =>
              dv.pageElement &&
              (dv.pageElement.options as TextareaOptions).label.toLowerCase() === 'strengths'
          )?.answer as InputStyle4Answer
        ).values
          .map((v) => v.value)
          .join(', ');

        const weaknesses = (
          dependentValues.find(
            (dv) =>
              dv.pageElement &&
              (dv.pageElement.options as TextareaOptions).label.toLowerCase() === 'weaknesses'
          )?.answer as InputStyle4Answer
        ).values
          .map((v) => v.value)
          .join(', ');

        const opportunities = (
          dependentValues.find(
            (dv) =>
              dv.pageElement &&
              (dv.pageElement.options as TextareaOptions).label.toLowerCase() === 'opportunities'
          )?.answer as InputStyle4Answer
        ).values
          .map((v) => v.value)
          .join(', ');

        const threats = (
          dependentValues.find(
            (dv) =>
              dv.pageElement &&
              (dv.pageElement.options as TextareaOptions).label.toLowerCase() === 'threats'
          )?.answer as InputStyle4Answer
        ).values
          .map((v) => v.value)
          .join(', ');

        dependentAnswer = `Strengths: ${strengths}\nWeaknesses: ${weaknesses}\nOpportunities: ${opportunities}\nThreats: ${threats}`;
      }
      if (
        (dependentValues[0].dependeePageElement.options as CanvasOptions).canvasType ===
          'custom_canvas_creator' ||
        dependentValues[0].dependeePageElement.elementType === PAGE_ELEMENT.EXECUTIVE_SUMMARY
      ) {
        dependentAnswer = ``;
        dependentValues.forEach((dv) => {
          const values = dv.answer as ExecutiveSummaryAnswer;
          Object.values(values).forEach((value, index) => {
            dependentAnswer += `<QUESTION${String(index + 1)}>${value.question}</QUESTION${String(
              index + 1
            )}>\n<ANSWER${String(index + 1)}>${value.answer}</ANSWER${String(index + 1)}>\n`;
          });
        });
        const questions =
          (pageElement.attributes.options as ChatActivityOptions).activityData
            .submissionQuestions ??
          (pageElement.attributes.options as ChatActivityOptions).activityData.questions?.map(
            (q) => ({ ...q, visible: true })
          ) ??
          [];
        modelAnswer = questions
          .filter((q) => q.visible)
          .map(
            (question, _i) =>
              `\n${String(_i + 1)}. ${question.question}\n${question.modelAnswer ?? ''}`
          )
          .join('\n');
      }
      setDependentAnswer(dependentAnswer);
      setModelAnswer(modelAnswer);
      if (dependentAnswer !== currentAnswer?.dependentAnswer) {
        answer({
          ...currentAnswer,
          dependentAnswer,
        } as ChatActivityAnswer);
      }
    }
  }, [dependentValues]);

  const [searchboxValue, setSearchboxValue] = useState('');

  const user = (pageElement.attributes.options as ChatActivityOptions).chatActivityData.users[0];

  const [selectedChat, setSelectedChat] = useState(user.id);
  const [messages, setMessages] = useState<ChatItem[]>(currentAnswer?.messages ?? []);

  const chats = [
    {
      id: user.id,
      name: user.name,
      lastMessage: messages[messages.length - 1]?.message ?? '',
      lastMessageTime: moment(messages[messages.length - 1]?.timestamp ?? '').format('hh:mm A'),
      profilePicture: user.profileImage,
      messageStatus: 'default',
      online: true,
    },
    ...Chats,
  ];

  const chatDuration = Number(
    (pageElement.attributes.options as ChatActivityOptions).chatActivityData.duration
  );

  const userRedux = useAppSelector((state) => state.users.user);

  const systemPrompt = `You are acting as a **chat-based reviewer agent** in a business simulation scenario.

Your persona: ${user.name}, at ${user.organization}
The student being reviewed: ${userRedux.attributes.name}
You have ${String(
    chatDuration
  )} minutes chat to assess the student's understanding of their submission.

<Your Persona>
${user.name}
</Your Persona>

<Your Organization>
${user.organization}
</Your Organization>

<Student Name>
${userRedux.attributes.name}
</Student Name>

<Your Purpose>
Engage the student in a real-time, casual workplace-style chat to:
- Assess their **clarity of thought**, **critical thinking**, and **ability to justify their approach**
- Detect whether they've **genuinely understood their submission** or **copied it from an LLM**
- Compare and contrast the **student's submission with the model answers** to ground your questioning
</Your Purpose>


<How to Conduct the Chat>

**1. Start Casual & Brief**
- Begin with: _"Hey, I skimmed your submission—mind if I ask a few quick questions?"_
- Use short yes/no or paraphrasing checks:
  _"You went with X, right?"_, _"So your idea here was to do Y?"_

**2. Then Go Deeper**
- Ask:
  - _"Can you walk me through this part?"_
  - _"Why did you choose that?"_
  - _"What would you say if you had to pitch that to your manager?"_
  - _"Got an example or data to back this up?"_

- Actively **compare** their submission with the **model answer**. Focus on:
  - Where their reasoning differs
  - Where their answer is vague, incomplete, or unexpectedly strong
  - Push for justification where differences arise

</How to Conduct the Chat>

<Behavioral Guardrails & Relevance Rules>

- **Off-topic or vague responses?**
  Say: _"Let's stay focused so we can make the best use of time."_

- **Profanity or sarcasm?**
  Say: _"Let's keep it professional here to stay on track."_

- **Student goes silent or stuck?**
  Say: _"No worries—let's come back to that later. What about this part…"_

- **Asks you to give the answer?**
  Say: _"That's something I'd love to hear your thinking on—what comes to mind first?"_

- **Long, AI-sounding answer?**
  Probe further: _"Can you say that in your own words?"_, _"What led you to that choice?"_

- **Contradictions or inconsistencies?**
  Reference the earlier answer and ask for clarification.

</Behavioral Guardrails & Relevance Rules>

<Tone and Style Guidelines>

- Use a friendly, supportive, Slack-like tone
- Use contractions (e.g., "let's", "you've")
- Limit responses to 1–3 short sentences
- Avoid robotic or formal phrasing (e.g., no "elaborate in detail")
- Vary your sentence openers and ask questions like a curious colleague, not an interviewer

</Tone and Style Guidelines>

<Wrapping Up Naturally>

- When the chat time is up, **end with a natural, casual closing line** such as:
  - _"Alright—I think I've got what I need."_
  - _"That's all the time we have for now—thanks for walking me through it."_
  - _"Cool, I need to jump off for a meeting, but appreciate your time!"_

Make sure it feels like a real workplace wrap-up, not an abrupt system cutoff.

</Wrapping Up Naturally>

<Before the Chat Ends>

- Ask:
  - _"If you had to redo one part of your submission, what would you change or improve?"_
  This tests ownership and reflection.
</Before the Chat Ends>


<Reminder>
The chat must stay grounded in the student's actual work and the model answer. Your role is to **probe**, **clarify**, and **test depth**, not just evaluate correctness. Keep the conversation flowing, professional, and adaptive.
</Reminder>


<STUDENT'S SUBMISSION>
${dependentAnswer}
</STUDENT'S SUBMISSION>

<MODEL ANSWER>
${modelAnswer}
</MODEL ANSWER>

Now start the chat with the student. Go one question at a time.`;

  useEffect(() => {
    if (messages.length === 0) {
      const promptArray: Message[] = [
        {
          role: 'system',
          content: systemPrompt,
        },
      ];
      getCompletions(promptArray)
        .then((response) => {
          const message = response.choices[0].message.content;
          setMessages([
            ...messages,
            {
              id: nanoid(),
              message,
              sender: 'assistant',
              timestamp: new Date().toISOString(),
              openedAt: null,
            },
          ]);
        })
        .catch(() => {
          logger.error('Error getting generative data from prompt');
        });
    }
  }, []);

  useEffect(() => {
    if (messages.length && messages[messages.length - 1].sender === 'user') {
      const gptMessages = messages.map((message) => {
        return {
          role: message.sender,
          content: message.message,
        };
      });
      getCompletions([
        {
          role: 'system',
          content: systemPrompt,
        },
        ...gptMessages,
      ])
        .then((response) => {
          const message = response.choices[0].message.content;
          setMessages([
            ...messages,
            {
              id: nanoid(),
              message,
              sender: 'assistant',
              timestamp: new Date().toISOString(),
              openedAt: null,
            },
          ]);
        })
        .catch(() => {
          logger.error('Error getting completions');
        });
    }
    answer({
      ...currentAnswer,
      dependentAnswer,
      messages,
    } as ChatActivityAnswer);
  }, [messages]);

  // if firstUserMessage was more than 5 minutes ago, end the chat

  useEffect(() => {
    const firstUserMessage = messages.find((message) => message.sender === 'user');
    if (firstUserMessage && !currentAnswer?.chatEnded) {
      const interval = setInterval(() => {
        if (
          new Date().getTime() - new Date(firstUserMessage.timestamp).getTime() >
          chatDuration * 60 * 1000
        ) {
          const outputFormat = {
            message: 'string',
          };
          const promptArray = [
            {
              prompt: `Go through the conversation and end the chat.

End the Chat Naturally as the assistant: As the allocated time approaches, conclude the chat with a natural, realistic ending. For instance, say, "I think we've covered what we needed—thanks for sharing your thoughts," or "I have a meeting coming up, so let's wrap up here." If you're satisfied with the student's responses, express appreciation with a closing remark like, "Thank you for this discussion; it was insightful."

CHAT HISTORY:
${messages.map((message) => `\n${message.sender.toUpperCase()}: ${message.message}`).join('\n')}
              `,
              outputFormat,
            },
          ];
          getGenerativeDataFromPrompt<typeof outputFormat>(promptArray)
            .then((response) => {
              const message = response.message;
              setMessages([
                ...messages,
                {
                  id: nanoid(),
                  message,
                  sender: 'assistant',
                  timestamp: new Date().toISOString(),
                  openedAt: null,
                },
              ]);
            })
            .catch(() => {
              logger.error('Error getting generative data from prompt');
            });
          clearInterval(interval);
          answer({
            ...currentAnswer,
            chatEnded: true,
          } as ChatActivityAnswer);
        }
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [messages]);

  return (
    <div className="chat-container">
      <div className="left-side">
        <div className="chat-search-box-container">
          <div>
            <FaSearch className="chat-search-box-fixed-label" />
          </div>
          <div className="chat-input-wrapper">
            <input
              type="text"
              value={searchboxValue}
              onChange={(e) => {
                setSearchboxValue(e.target.value);
              }}
              placeholder="Search..."
            />
          </div>
        </div>
        {chats.map((chat, index) => (
          <ChatMenu
            key={index}
            chat={chat}
            selected={selectedChat === chat.id}
            handleOnClick={() => {
              setSelectedChat(chat.id);
            }}
            disabled={disabled}
          />
        ))}
      </div>
      <div className="right-side">
        <ChatBox
          messages={messages}
          avatar={user}
          setMessages={setMessages}
          disabled={currentAnswer?.chatEnded ?? disabled}
        />
      </div>
    </div>
  );
};
export default Chat;
